import { AxiosError } from 'axios';

import { captureException } from '@sentry/react';
import type { WriteContractErrorType } from '@wagmi/core';

import {
  ERROR_MESSAGE_MAP,
  ERROR_WAGMI_MESSAGE_MAP,
  ErrorWagmiMap,
  ServerError,
} from '@constants/common/error.constants';
import { ErrorMessages } from '@constants/common/messages.constants';

// Hardware wallets reject too-long sign-message payloads with these errors —
// detect them to fall back to the short hash-based disclaimer attestation.
const HW_OVERFLOW_PATTERNS: readonly RegExp[] = [
  /bytes overflow/i,
  /data is too long/i,
  /message too long/i,
  /payload.{0,20}exceed/i,
];

export const isHwOverflowError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return HW_OVERFLOW_PATTERNS.some((pattern) => pattern.test(message));
};

export const mapWagmiError = (error: WriteContractErrorType | Error | null) => {
  if (!error) return ErrorMessages.UnknownError;

  const { message } = error;

  const errorEntries = Object.entries(ErrorWagmiMap);
  const matchedEntry = errorEntries.find(([_, value]) =>
    message.includes(value),
  );

  if (matchedEntry) {
    const [, value] = matchedEntry;
    captureException(error);
    return ERROR_WAGMI_MESSAGE_MAP[value];
  }

  captureException(error);
  return ErrorMessages.DeniedTransaction;
};

export const parseAxiosError = (error: AxiosError): ErrorMessages => {
  const serverMessage = (error.response?.data as { message?: string })
    ?.message as ServerError;
  captureException(error);

  return serverMessage
    ? ERROR_MESSAGE_MAP[serverMessage] || ErrorMessages.UnknownError
    : ErrorMessages.UnknownError;
};
